import { Link, createFileRoute } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { AdminCompanyActions } from "@/components/admin-company-actions"
import { AdminRow } from "@/components/admin/admin-list-page"
import { AdminPersonLine } from "@/components/admin/admin-person-line"
import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/page-header"
import { StatTile } from "@/components/stat-tile"
import { StatusPill } from "@/components/status-pill"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

export const Route = createFileRoute("/admin/")({
  component: AdminOverviewPage,
})

function AdminOverviewPage() {
  const overview = useQuery(api.admin.overview)

  if (overview === undefined) {
    return <Skeleton className="h-48 w-full" />
  }

  const stats = [
    { label: "Pending companies", value: overview.pendingCompanies, to: "/admin/companies" },
    { label: "Open jobs", value: overview.openJobs, to: "/admin/jobs" },
    { label: "Seekers", value: overview.seekers, to: "/admin/people" },
    { label: "Paid seekers", value: overview.paidLifetime, to: "/admin/people" },
    { label: "Failed payments", value: overview.failedPayments, to: "/admin/payments" },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        level={2}
        title="Overview"
        subtitle="Platform health at a glance."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <Link key={stat.label} to={stat.to} className="block">
            <Card className="transition-colors hover:border-[color:var(--wf-ink)]/20">
              <CardContent className="pt-6">
                <StatTile size="lg" label={stat.label} value={stat.value} />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <section>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-bold text-[color:var(--wf-ink)]">
            Pending verification
          </h3>
          <Link to="/admin/companies" className="text-sm font-semibold text-[color:var(--wf-lime-text)]">
            View all
          </Link>
        </div>
        <div className="mt-4 space-y-3">
          {overview.pendingQueue.length === 0 && (
            <EmptyState variant="plain" title="No companies waiting for review." />
          )}
          {overview.pendingQueue.map((c) => (
            <AdminRow
              key={c._id}
              title={c.name || "(no company name)"}
              badges={
                <StatusPill domain="verification" status="pending" as="badge" />
              }
              meta={
                <AdminPersonLine
                  name={c.ownerName}
                  email={c.ownerEmail}
                  linkedinUrl={c.ownerLinkedinUrl}
                />
              }
              actions={
                <AdminCompanyActions
                  companyId={c._id}
                  verificationStatus={c.verificationStatus}
                />
              }
            >
              {c.website && (
                <a
                  href={c.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"
                >
                  {c.website}
                </a>
              )}
            </AdminRow>
          ))}
        </div>
      </section>
    </div>
  )
}
